import React from "react";
import { ArrowRightIcon } from "lucide-react";
import type { CaseStudyContent, ProcessStep } from "../../data/caseStudies";
import { CaseStudySection } from "./CaseStudySection";
import { BODY, EYEBROW, H3 } from "../../utils/typography";
type ProcessSectionProps = {
  process: CaseStudyContent["process"];
};
export function ProcessSection({
  process
}: ProcessSectionProps) {
  return <CaseStudySection label="The Process" data-id="element-127" data-mp="1w33jwmdrkz2v">
      {process.title && <p className={`mb-10 ${EYEBROW} text-rust`} data-id="element-128" data-mp="1w5ppigh3h4ig">{process.title}</p>}

      {process.layout === "timeline" ? <ProcessTimeline steps={process.steps} data-id="element-129" data-mp="261ss1nfuz55x" /> : <ol className="grid grid-cols-1 gap-x-10 gap-y-10 md:grid-cols-2" data-id="element-130" data-mp="261anm3j1lkmx">
          {process.steps.map((step, i) => <li key={step.title} data-id="element-131" data-mp="210mwlhutd3sp" data-mpi={i}>
              <p className="text-sm text-espresso/70" data-id="element-132" data-mp="bn9orinhsxmu">
                {String(i + 1).padStart(2, "0")} <span aria-hidden="true" data-id="element-133" data-mp="6oyex7x76n4m">/</span>
              </p>
              <h3 className={`mt-2 ${H3} text-espresso`} data-id="element-134" data-mp="6qjdd8mmuq7r">{step.title}</h3>
              <p className={`mt-3 ${BODY} text-espresso/70`} data-id="element-135" data-mp="217ul4mcwrh70">{step.body}</p>
            </li>)}
        </ol>}

      {process.flow && <ol aria-label="Journey" className="mt-12 flex flex-wrap items-center gap-x-2 gap-y-3" data-id="element-136" data-mp="2a79djrcmy1u5">
          {process.flow.map((stage, i) => <li key={stage} className="flex items-center gap-2" data-id="element-137" data-mp="2eyzn3v3r0lki" data-mpi={i}>
              <span className="whitespace-nowrap rounded-full bg-paper px-4 py-2 text-sm font-medium text-espresso" data-id="element-138" data-mp="2jxay495s524b">
                {stage}
              </span>
              {i < process.flow!.length - 1 && <ArrowRightIcon className="h-4 w-4 text-espresso/40" aria-hidden="true" data-id="element-139" data-mp="2tuyvz7yompqp" />}
            </li>)}
        </ol>}

      {process.closing && <p className={`mt-12 max-w-2xl ${H3} text-espresso`} data-id="element-140" data-mp="zhh6hfepvit1">
          {process.closing}
        </p>}
    </CaseStudySection>;
}

/** Sequential stages on a connected vertical track; a stage can carry guiding questions and an example. */
function ProcessTimeline({
  steps
}: {
  steps: ProcessStep[];
}) {
  return <ol data-id="element-141" data-mp="21vf2lnryullm">
      {steps.map((step, i) => {
      const last = i === steps.length - 1;
      return <li key={step.title} className="grid grid-cols-[2.5rem_minmax(0,1fr)] gap-x-5 md:grid-cols-[3rem_minmax(0,1fr)] md:gap-x-8" data-id="element-142" data-mp="2fvfyspxad4ur" data-mpi={i}>
            <div className="flex flex-col items-center" data-id="element-143" data-mp="2psavd3a00v6e">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-espresso text-sm font-medium text-cream md:h-12 md:w-12" data-id="element-144" data-mp="2pqq69t6ge4fx">
                {String(i + 1).padStart(2, "0")}
              </span>
              {!last && <span aria-hidden="true" className="w-px flex-1 bg-espresso/20" data-id="element-145" data-mp="2ujj9gcm9x4pw" />}
            </div>

            <div className={last ? "" : "pb-12 md:pb-14"} data-id="element-146" data-mp="38isnb5n0bmqp">
              <h3 className={`pt-1.5 ${H3} text-espresso md:pt-2.5`} data-id="element-147" data-mp="38jbubrb4cqpy">{step.title}</h3>
              <p className={`mt-2 max-w-2xl ${BODY} text-espresso/70`} data-id="element-148" data-mp="33lk3z1q4hcnv">{step.body}</p>

              {step.questions && <div className="mt-8" data-id="element-149" data-mp="3n3ogjtak5hpm">
                  <p className={`${EYEBROW} text-espresso/70`} data-id="element-150" data-mp="3s5dnawqqz97w">Questions to answer first</p>
                  <ul className="mt-4 grid grid-cols-1 gap-x-10 gap-y-4 md:grid-cols-2" data-id="element-151" data-mp="3e5m4mipad4nk">
                    {step.questions.map(q => <li key={q} className={`${BODY} font-medium text-espresso`} data-id="element-152" data-mp="3i53dlm3x8jxh">
                        {q}
                      </li>)}
                  </ul>
                </div>}

              {step.example && <aside aria-label="Example" className="-mx-4 mt-8 rounded-3xl bg-rust px-4 py-6 md:-mx-8 md:px-8 md:py-8" data-id="element-153" data-mp="2uhdaw483gqly">
                  <p className={`${BODY} text-cream`} data-id="element-154" data-mp="2zdtzt0n4b903">{step.example}</p>
                </aside>}
            </div>
          </li>;
    })}
    </ol>;
}